import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const activeTab = searchParams.get('tab') || 'all';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  const activeCategory = categories.find((c) => c.slug === activeTab) || categories[0];

  const filteredComics = useMemo(() => {
    let result = getComicsByCategory(activeTab);
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(term) ||
          c.author.toLowerCase().includes(term) ||
          c.tags.some((t) => t.toLowerCase().includes(term))
      );
    }
    return result;
  }, [activeTab, searchTerm]);

  const handleTabChange = (slug: string) => {
    setSearchParams({ tab: slug });
    setSearchTerm('');
  };

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name}韩漫 - 韩漫屋`,
    description: `韩漫屋${activeCategory.name}分类，收录热门韩国漫画，高清画质免费阅读。${activeCategory.description}`,
    url: typeof window !== 'undefined' ? window.location.href : '',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫屋',
      url: typeof window !== 'undefined' ? window.location.origin : '',
    },
  };

  return (
    <>
      <SEO
        title={`${activeCategory.name}韩漫_在线观看下拉式韩漫_高清韩漫大全-韩漫屋`}
        description={`韩漫屋${activeCategory.name}分类提供全免费韩国漫画在线阅读，下拉式观看模式搭配高清画质，每日更新热门${activeCategory.name}类韩漫作品。`}
        keywords={`${activeCategory.name},韩国漫画,免费阅读,下拉式韩漫,高清大全,韩漫屋,在线观看`}
        canonicalUrl={`/category?tab=${activeTab}`}
        ogType="website"
        schema={categorySchema}
      />

      <div className="pt-20 pb-12 md:pb-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '分类浏览' },
            ]}
          />

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-2 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {activeCategory.name}韩漫
          </h1>
          <p className="text-sm text-foreground-500 mb-6">{activeCategory.description}</p>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-8 scrollbar-hide flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleTabChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-white shadow-[0_2px_8px_rgba(0,0,0,0.1)]'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <div className="relative max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={`搜索${activeCategory.name}韩漫...`}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">未找到相关韩漫作品</p>
              <p className="text-foreground-400 text-xs mt-1">试试其他关键词或分类</p>
            </div>
          )}

          <div className="mt-8 text-center">
            <p className="text-xs text-foreground-400">
              共找到 <strong className="text-foreground-600">{filteredComics.length}</strong> 部
              {activeTab !== 'all' ? activeCategory.name : ''}韩漫
            </p>
          </div>
        </div>
      </div>
    </>
  );
}